import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Input,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { ActivatedRoute } from '@angular/router';
import { ApprovalFlowHistoryDto, ApprovalState, Client, DistributionList, FdmEventDto, ReportType } from '@fdm/api-client';
import { MaterialModule } from '@fdm/material';
import { ApprovalFlowStateOptions, FDMStatusOptions, filterTable, getErrorMessage, ReportTypeOptions } from '@fdm/utils';
import { ToastrService } from 'ngx-toastr';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  concatMap,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  from,
  map,
  mergeMap,
  Observable,
  of,
  skip,
  switchMap,
  tap,
  toArray
} from 'rxjs';
import { ApprovalFlowService, ButtonScriptsService, FieldHighlightService, FieldLocation, FormValidationService, ReportFormPage, ReportFormStore, ReportsBulkSaveService, ReportTask } from '../../../../services';
import { AuthService, CommonUserService, EmailDescriptionService } from '@fdm/services';
import { approvalFlowConfig } from './approval-flow.config';
import { LoaderService } from '@fdm/services';
import { ReportForm } from '../../report-form';
import { MatTableDataSource } from '@angular/material/table';
import moment from 'moment';
import { emailValidator, FilterResponse, noDuplicates, TableColumnFilterComponent } from '@fdm/shared';
import { MatSort, MatSortModule } from '@angular/material/sort';

const ENDORSERS_MAX_LENGTH = 3;

@Component({
  selector: 'app-approval-flow-tab',
  standalone: true,
  imports: [
    CommonModule, 
    ReactiveFormsModule, 
    MaterialModule,

    // Components
    TableColumnFilterComponent
  ],
  templateUrl: './approval-flow-tab.component.html',
  styleUrl: './approval-flow-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApprovalFlowTabComponent extends ReportForm implements OnInit, AfterViewInit {
  @ViewChild(MatSort) sort!: MatSort;
  
  @Input() data: any = {};
  @Input() readonly: boolean = false;

  public form!: FormGroup;
  public override readonlyFields: string[] = ['srOpsConsultant'];

  historyDisplayedColumns: string[] = ['currentState', 'actionItemStatus', 'comment', 'type', 'created', 'createdBy'];
  historyDataSource: MatTableDataSource<ApprovalFlowHistoryDto>;

  private destroyRef = inject(DestroyRef);
  private apiClient = inject(Client);
  private route = inject(ActivatedRoute);
  private fb = inject(FormBuilder);
  private toastrService = inject(ToastrService);
  private cdr = inject(ChangeDetectorRef);
  private approvalFlowService = inject(ApprovalFlowService);
  private _bulkSave = inject(ReportsBulkSaveService);
  private loader = inject(LoaderService);
  protected _fieldHighlightService = inject(FieldHighlightService);
  protected FieldLocation = FieldLocation;
  private _formValidationService = inject(FormValidationService);
  public buttonScript = inject(ButtonScriptsService);
  private emailDescriptionService = inject(EmailDescriptionService);
  private _auth = inject(AuthService);
  private _commonUser = inject(CommonUserService);


  private dynamicVisibleFields = signal<string[]>([]);
  private formReady = signal(false);

  private _general = toSignal(this._store.general$);
  private reportType = toSignal(this._store.reportType$);
  private abcPrioritization = toSignal(this._store.abcPrioritization$);
  private consequenceRanking = toSignal(this._store.criticality$);
  private authorizationLevel = toSignal(this._store.assignedManagementAuthorizationLevel$);
  private currentStep = toSignal(this._store.page$);

  isArFlow = signal(false);
  isProcessing = signal(false);

  loadingFields = signal<{ [key: string]: boolean }>({});
  emailAutocompleteOptions = signal<{ [key: string]: string[] }>({});
  distributionListOptions = signal<DistributionList[]>([]);

  private generalTabId!: string;
  private approvalFlowTabId!: number;

  private _matchedDistribution: { to?: string; cc?: string } | null = null;

  private history$ = new BehaviorSubject<ApprovalFlowHistoryDto[]>([]);
  private fdmEvents$ = new BehaviorSubject<FdmEventDto[]>([]);

  readonly ENDORSERS_MAX_LENGTH = ENDORSERS_MAX_LENGTH;

  endorsersForm2 = this.fb.group({
    endorsers: this.fb.array([], [noDuplicates()])
  });

  get endorsers() {
    return this.endorsersForm2.get('endorsers') as FormArray<FormControl<string | null>>;
  }

  protected displayNames: { [key: string]: string } = {
    areaLeadInspEng: 'Area Lead Insp/Eng',
    areaLeadInspEngDescription: 'Area Lead Insp/Eng Description',
    inspEngSupervisor: 'Insp/Eng Supervisor',
    inspEngSupervisorDescription: 'Insp/Eng Supervisor Description',
    srOpsConsultant: 'Operations Advisor ',
    srOpsConsultantDescription: 'Operations Advisor Description',
    localTechnicalAuthority: ' Local Technical Authority',
    localTechnicalAuthorityDescription:
      ' Local Technical Authority Description',
    assetTypeHCEPO:
      'Asset Type HCEPO (RE Manager or Automation Superintendent)',
    assetTypeHCEPODescription: 'Asset Type HCEPO Description',
    areaOpsSuperintendent: 'Area Ops Superintendent',
    areaOpsSuperintendentDescription: 'Area Ops Superintendent Description',
    productionOperationManager: 'Production Operation Manager',
    productionOperationManagerDescription:
      'Production Operation Manager Description',
    generalManagerOperation: 'General Manager Operation',
    generalManagerOperationDescription: 'General Manager Operation Description',
    generalDirectorOperation: 'General Director',
    generalDirectorOperationDescription: 'General Director Description',
    informedEmailsTo: 'Informed Emails (To)',
    informedEmailsCc: 'Informed Emails (CC)',
  };

  public get step() {
    return ReportFormPage.APPROVAL_FLOW;
  }

  constructor() {
    super();

    this.historyDataSource = new MatTableDataSource([] as ApprovalFlowHistoryDto[]);

    effect(() => {
      if (!this.formReady()) {
        return;
      }
      this.diplayElementControl();

      if (this.reportType() !== ReportType.AnomalyReport) {
        this._updateSrOpsConsultantWithDescription(this._general()?.owner);
      }
    });

    effect(() => {
      if (!this.formReady()) {
        return;
      }

      this._applyDistributionFieldsEditability();
    });
  }

  stepValueOptions: { [key: string]: string } = {};
  private labelMaps(): void {
    this.stepValueOptions = ApprovalFlowStateOptions.reduce((acc, curr) => {
      acc[curr.value as unknown as string] = curr.label;
      return acc;
    }, {} as { [key: string]: string });
  }

  diplayElementControl() {

    const consequenceRanking = this.consequenceRanking();
    const abcPrioritization = this.abcPrioritization();
    const reportType = this.reportType();
    const authorizationLevel = this.authorizationLevel();
    if (reportType == undefined)
      return;

    const get = (name: string) => this.form.get(name);

    const isIntegrityOrReliabilityThreat =
      reportType === ReportType.IntegrityThreat ||
      reportType === ReportType.ReliabilityThreat;
    const isDeficienies =
      reportType === ReportType.ReliabilityDeficiency ||
      reportType === ReportType.IntegrityDeficiency;
    const isAbcPrioritizationA = abcPrioritization === 'A';

    const shouldShowAssetTypeHCEPO =
      isIntegrityOrReliabilityThreat && isAbcPrioritizationA;

    const dynamicSet = new Set<string>();
    const addDynamic = (name: string) => dynamicSet.add(name);
    const removeDynamic = (name: string) => dynamicSet.delete(name);

    const setRequired = (control: AbstractControl | null, name?: string) => {
      control?.setValidators([Validators.required, Validators.maxLength(255)]);
      if (name) addDynamic(name);
    };
    const setOptional = (control: AbstractControl | null, name?: string) => {
      control?.clearValidators();
      control?.setValidators([Validators.maxLength(255)]);
      if (name) removeDynamic(name);
    };
    const setRequiredByName = (name: string) => setRequired(get(name), name);
    const setHidebyName = (name: string) => setOptional(get(name), name);

    let dynamicFields: string[] = [];
    if (reportType === ReportType.AnomalyReport) {
      ['inspEngSupervisor', 'areaLeadInspEng']
        .forEach(setRequiredByName);
    }
    else {
      ['inspEngSupervisor', 'srOpsConsultant']
        .forEach(setRequiredByName);
    }

    this.isArFlow.set(reportType !== ReportType.AnomalyReport);

    // this.isArFlow.set(true);

    switch (reportType) {
      case ReportType.IntegrityThreat:
      case ReportType.ReliabilityThreat:
        setRequiredByName('assetTypeHCEPO');
        setRequiredByName('areaOpsSuperintendent');
        break;

      case ReportType.AnomalyReport:
        ['localTechnicalAuthority', 'areaOpsSuperintendent', 'productionOperationManager',
          'generalManagerOperation', 'generalDirectorOperation', 'assetTypeHCEPO']
          .forEach(setHidebyName);
        break;

      case ReportType.TaskDeferral:
        setHidebyName('localTechnicalAuthority');
        switch (authorizationLevel) {
          case 'A':
            setRequiredByName('areaOpsSuperintendent');
            break;
          case 'B':
            ['areaOpsSuperintendent', 'productionOperationManager']
              .forEach(setRequiredByName);

            break;
          case 'C':
            ['areaOpsSuperintendent', 'productionOperationManager', 'generalManagerOperation']
              .forEach(setRequiredByName);
            break;
          case 'D':
            ['areaOpsSuperintendent', 'productionOperationManager', 'generalManagerOperation', 'generalDirectorOperation']
              .forEach(setRequiredByName);
            break;
        }
        break;
    }

    const pushAssetType = () => {
      if (reportType !== ReportType.AnomalyReport) {
        if (isIntegrityOrReliabilityThreat) {
          if (abcPrioritization === 'A') {
            setRequiredByName('assetTypeHCEPO');
          } else if (abcPrioritization != undefined) {
            setHidebyName('assetTypeHCEPO');
          }
        }
        else {
          setRequiredByName('assetTypeHCEPO');
        }
      }
    };

    switch (consequenceRanking) {
      case 'IC3':
        pushAssetType();
        if (isDeficienies) {
          ['areaOpsSuperintendent', 'productionOperationManager']
            .forEach(setRequiredByName);

        }
        break;

      case 'IC2':
      case 'AC2':
        if (isDeficienies) {
          ['localTechnicalAuthority', 'areaOpsSuperintendent', 'productionOperationManager', 'generalManagerOperation']
            .forEach(setRequiredByName);
        }
        pushAssetType();
        break;

      case 'IC1':
      case 'AC1':
        if (isDeficienies) {
          ['localTechnicalAuthority', 'areaOpsSuperintendent', 'productionOperationManager', 'generalManagerOperation', 'generalDirectorOperation']
            .forEach(setRequiredByName);
        }
        pushAssetType();
        break;

      default:

        const isCritical =
          consequenceRanking === 'IC3' ||
          consequenceRanking === 'IC2' ||
          consequenceRanking === 'IC1' ||
          consequenceRanking === 'AC1' ||
          consequenceRanking === 'AC2';

        if (isIntegrityOrReliabilityThreat && !isCritical && consequenceRanking != undefined) {

          setHidebyName('assetTypeHCEPO');
        }

        if (reportType !== ReportType.TaskDeferral) {
          ['localTechnicalAuthority', 'productionOperationManager',
            'generalManagerOperation', 'generalDirectorOperation', 'assetTypeHCEPO']
            .forEach(setHidebyName);
        }

        if (isDeficienies) {
          ['areaOpsSuperintendent'].forEach(setRequiredByName);
        }
    }

    this.dynamicVisibleFields.update(() => Array.from(dynamicSet));
    [
      'areaLeadInspEng',
      'inspEngSupervisor',
      'srOpsConsultant',
      'localTechnicalAuthority',
      'assetTypeHCEPO',
      'areaOpsSuperintendent',
      'productionOperationManager',
      'generalManagerOperation',
      'generalDirectorOperation',
    ].forEach((n) => get(n)?.updateValueAndValidity({ emitEvent: false }));

  }

  override ngOnInit(): void {
    this.labelMaps();
    this.initializeForm();
    super.ngOnInit();
    this.formReady.set(true);
    this._initTriggers();
    this._initFormTriggers();
    this._initOwnerSync();

    this.approvalFlowService.flowEventsUpdated
      .pipe(
        switchMap(() => this._getTabInfo()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();


    if (this.reportType() !== ReportType.AnomalyReport) {
      this._updateSrOpsConsultantWithDescription(this._general()?.owner);
    }

  }

  ngAfterViewInit(): void {
    this.historyDataSource.sort = this.sort;
  }

  canShowDistributionListDropdown = computed(() => {
    const isAdmin = this._auth.isAdmin();
    if (isAdmin) return true;
    const general = this._general();
    const isInitiator = general?.initiator != null && this._commonUser.matchesWithField(general.initiator);
    const isOwner = general?.owner != null && this._commonUser.matchesWithField(general.owner);
    const isBackToBack = this.distributionListOptions().some(item =>
      item.creatorBackToBackEmail != null && this._commonUser.matchesWithField(item.creatorBackToBackEmail)
    );
    return isInitiator || isOwner || isBackToBack;
  });

  isDistributionSelectionDisabled = computed(() => {
    const currentState = this._general()?.currentState;
    return [
      ApprovalState.PlanApproved,
      ApprovalState.ResolutionPlanCompleted,
      ApprovalState.ValidationAndVerificationCompleted,
      ApprovalState.Closed,
      ApprovalState.Cancelled,
    ].includes(currentState as ApprovalState);
  });

  canEditDistributionList = computed(() => {
    return this.canShowDistributionListDropdown() && !this.isDistributionSelectionDisabled();
  });

  private visibleFields = computed(() => {
    const reportType = this.reportType();
    return reportType ? approvalFlowConfig[reportType] || [] : [];
  });

  protected override onPolicyOrFormStateChanged(): void {
    this._applyDistributionFieldsEditability();
  }

  isFieldVisible(fieldName: string): boolean {
    return this.visibleFields().includes(fieldName) || this.dynamicVisibleFields().includes(fieldName);
  }

  isRequired(fieldName: string): boolean {
    const control = this.form.get(fieldName);

    if (!control) {
      return false;
    }

    if (control.validator) {
      const validator = control.validator({} as AbstractControl);
      if (validator && validator['required']) {
        return true;
      }
    }
    return false;
  }

  reportApprovalVisibility() {
    if (this.reportType() === ReportType.AnomalyReport) {
      return false;
    }
    else if (this.reportType() === ReportType.TaskDeferral) {
      if (!this.authorizationLevel()) {
        return false;
      }
      else
        return true;
    }
    else
      return true;
  }

  initializeForm(): void {
    this.form = this.fb.group({
      areaLeadInspEng: ['', [Validators.maxLength(255)]],
      areaLeadInspEngDescription: ['', Validators.maxLength(20000)],
      inspEngSupervisor: ['', [Validators.maxLength(255)]],
      inspEngSupervisorDescription: ['', Validators.maxLength(20000)],
      srOpsConsultant: [{ value: '', disabled: true }, [Validators.maxLength(255)]],
      srOpsConsultantDescription: ['', Validators.maxLength(20000)],
      localTechnicalAuthority: ['', [Validators.maxLength(255)]],
      localTechnicalAuthorityDescription: ['', Validators.maxLength(20000)],
      assetTypeHCEPO: ['', [Validators.maxLength(255)]],
      assetTypeHCEPODescription: ['', Validators.maxLength(20000)],
      areaOpsSuperintendent: ['', [Validators.maxLength(255)]],
      areaOpsSuperintendentDescription: ['', Validators.maxLength(20000)],
      productionOperationManager: ['', [Validators.maxLength(255)]],
      productionOperationManagerDescription: ['', Validators.maxLength(20000)],
      generalManagerOperation: ['', [Validators.maxLength(255)]],
      generalManagerOperationDescription: ['', Validators.maxLength(20000)],
      generalDirectorOperation: ['', [Validators.maxLength(255)]],
      generalDirectorOperationDescription: ['', Validators.maxLength(20000)],
      informedEmailsTo: ['', [Validators.maxLength(2000)]],
      informedEmailsCc: ['', [Validators.maxLength(2000)]],
      id: [''],
      additionalEndorser1: [''],
      additionalEndorser2: [''],
      additionalEndorser3: [''],
    });

    this._formValidationService.registerForm(ReportFormPage.APPROVAL_FLOW, this.form);
  }

  getErrorMessage(fieldName: string): string | null {
    return getErrorMessage(
      this.form,
      fieldName,
      this.displayNames
    );
  }

  private _initFormTriggers(): void {
    this.setupEmailToDescriptionBinding(
      'areaLeadInspEng',
      'areaLeadInspEngDescription'
    );
    this.setupEmailToDescriptionBinding(
      'inspEngSupervisor',
      'inspEngSupervisorDescription'
    );
    this.setupEmailToDescriptionBinding(
      'srOpsConsultant',
      'srOpsConsultantDescription'
    );
    this.setupEmailToDescriptionBinding(
      'localTechnicalAuthority',
      'localTechnicalAuthorityDescription'
    );
    this.setupEmailToDescriptionBinding(
      'assetTypeHCEPO',
      'assetTypeHCEPODescription'
    );
    this.setupEmailToDescriptionBinding(
      'areaOpsSuperintendent',
      'areaOpsSuperintendentDescription'
    );
    this.setupEmailToDescriptionBinding(
      'productionOperationManager',
      'productionOperationManagerDescription'
    );
    this.setupEmailToDescriptionBinding(
      'generalManagerOperation',
      'generalManagerOperationDescription'
    );
    this.setupEmailToDescriptionBinding(
      'generalDirectorOperation',
      'generalDirectorOperationDescription'
    );
  }

  private _initTriggers(): void {

    this._getDistributionlist();
    this._loadDistributionListTitles();

    this.route.paramMap
      .pipe(
        switchMap((params) => {
          const id = params.get('id') ?? '';

          if (id) {
            this.generalTabId = id;
            return this._getTabInfo();
          }

          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this._store.page$
      .pipe(
        filter((page) => page === this.step),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this._store.setPageValidity({ page: this.step, isStepValid: this.form.valid });
      });

    this.form.valueChanges
      .pipe(
        filter(() => this.step === this.currentStep()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((value) => {

        const isFormValid = this.form.valid;

        this._store.setPageValidity({ page: this.step, isStepValid: isFormValid });

        if (isFormValid) {
          const payload: ReportTask = {
            page: this.step,
            request: this._save()
          };

          if (this._bulkSave.includes(this.step)) {
            this._bulkSave.update(payload);
          } else {
            this._bulkSave.add(payload);
          }
        }
      });

    combineLatest([this.history$, this.fdmEvents$, this._store.ript$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([history, fdmEvents, ript]) => {
        const isRiptCreated = !!ript?.id;

        // Transform history data
        const transformedHistory = history
          // .filter((item) => item.currentState !== ApprovalState.Draft)
          .map(item => this._transformHistoryItem(item, isRiptCreated, false));

        // Transform fdmEvents data
        const transformedFdmEvents = fdmEvents
          .map(item => this._transformHistoryItem(item, isRiptCreated, true));

        // Combine both arrays and sort by created date (descending)
        const combinedData = [...transformedHistory, ...transformedFdmEvents]
          .sort((a, b) => {
            const dateA = moment(a.created);
            const dateB = moment(b.created);

            // Handle invalid dates - put them at the end
            if (!dateA.isValid() && !dateB.isValid()) return 0;
            if (!dateA.isValid()) return 1;
            if (!dateB.isValid()) return -1;

            // Sort descending (newest first)
            return dateB.diff(dateA);
          });

        this.historyDataSource.data = combinedData;
        this.tempTableData = [...this.historyDataSource.data];
        this.cdr.markForCheck();
      });

    this.endorsers.valueChanges
      .pipe(
        debounceTime(500),
        filter(() => this.step === this.currentStep()),
        switchMap(() => {
          return from(this.endorsers.controls)
            .pipe(
              concatMap((endorser) => {                    
                // const email = this.endorsersForm.get(key)?.value;
                return endorser.value && endorser.invalid
                  ? this.apiClient.email(endorser.value.trim()).pipe(
                      map(res => {
                        const preferredEmail = this.emailDescriptionService.getPreferredEmailForApprovalFlow(res);
                        
                        return preferredEmail ? preferredEmail : endorser.value!.trim();
                      }),
                      catchError(err => {                            
                        return of(err);
                      })
                    )
                  : of(endorser.value)
              }),
              toArray()
            )
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((emails) => {
        
        const isFormValid = this.endorsers.valid;
        
        const oldEmails = this.endorsers.value;
        emails.forEach((email, idx) => {
          if (email !== oldEmails[idx]) {
            this.endorsers.at(idx).setValue(email);
          }
        });

        const formData = this.form.value;

        const existingEndorsers = [
          formData.additionalEndorser1?.toLowerCase(),
          formData.additionalEndorser2?.toLowerCase(),
          formData.additionalEndorser3?.toLowerCase(),
        ].filter(Boolean);

        const newEmails = emails.filter(email => !existingEndorsers.includes(email?.toLowerCase()));

        this._store.setPageValidity({ page: this.step, isStepValid: isFormValid });

        if (isFormValid) {
          newEmails.forEach((email, idx) => {
            if (email) {
              const flag = `email-${idx}`;
              const payload: ReportTask<string> = {
                page: flag,
                request: this.apiClient.additionalEndorser(this.generalTabId, email).pipe(
                  switchMap(() => this._getTabInfo()),
                  tap(() => {
                    this.endorsers.at(idx).disable({ emitEvent: false });
                  })
                )
              };

              if (this._bulkSave.includes(flag)) {
                this._bulkSave.update(payload);
              } else {
                this._bulkSave.add(payload);
              }
            }
          });


        }
      });
  }

  private _applyDistributionFieldsEditability(): void {
    if (!this.form) {
      return;
    }

    const informedEmailsToControl = this.form.get('informedEmailsTo');
    const informedEmailsCcControl = this.form.get('informedEmailsCc');

    if (!informedEmailsToControl || !informedEmailsCcControl) {
      return;
    }

    if (this.canEditDistributionList()) {
      informedEmailsToControl.enable({ emitEvent: false });
      informedEmailsCcControl.enable({ emitEvent: false });
    } else {
      informedEmailsToControl.disable({ emitEvent: false });
      informedEmailsCcControl.disable({ emitEvent: false });
    }
  }

  private _save() {
    return this._handleSave().pipe(
      switchMap(() => this._getTabInfo())
    );
  }

  private _handleSave(): Observable<any> {
    const values = this.form.getRawValue();

    const handleError = (error: any) => {
      this.toastrService.error(`${error.message}`);
      this.loader.stopProcessing();
      return of(null);
    };

    const payload = {
      ...values,
      additionalEndorser1: values.additionalEndorser1 ? values.additionalEndorser1 : undefined,
      additionalEndorser2: values.additionalEndorser2 ? values.additionalEndorser2 : undefined,
      additionalEndorser3: values.additionalEndorser3 ? values.additionalEndorser3 : undefined,
      generalTabId: this.generalTabId ?? undefined
    }

    return values.id && values.id > 0
      ? this.approvalFlowService.update(payload).pipe(
        tap(() => {
          // hide notification for closing
          if (this._general()?.currentState !== ApprovalState.ValidationAndVerificationCompleted) {
            this.toastrService.success('Approval Flow updated successfully');
          }

          this.loader.stopProcessing();
        }),
        catchError(handleError)
      )
      : this.approvalFlowService.save(payload).pipe(
        map(res => res.toString()),
        tap(() => {
          this.toastrService.success('Approval Flow saved successfully');
          this.loader.stopProcessing();
        }),
        catchError(handleError)
      );
  }

  public _getTabInfo(): Observable<any> {
    if (!this.generalTabId) {
      return of(null);
    }

    return combineLatest([
      this.apiClient.byGeneralTab(this.generalTabId),
      this._getFdmEvents()
    ]).pipe(
      tap(([data, fdmEvents]) => {
        this.form.patchValue(data, { emitEvent: false });

        // Saved values may be empty; auto-fill informed emails from the matched distribution list.
        this._applyDistributionDefaultsIfEmpty();

        if (this.reportType() !== ReportType.AnomalyReport) {
          this._updateSrOpsConsultantWithDescription(this._general()?.owner);
        }

        this._store.setApprovalFlow(data);

        this.clearEndorsers();

        [data.additionalEndorser1, data.additionalEndorser2, data.additionalEndorser3].forEach(email => {
          if (email) {
            this.addNewEndorser(email, true);
          }
        })

        // Update both history streams
        this.history$.next(data?.history ?? []);
        this.fdmEvents$.next(fdmEvents ?? []);
      }),
      map(([data]) => data)
    );
  }


  private _getFdmEvents(): Observable<FdmEventDto[]> {
    if (!this.generalTabId) {
      return of([]);
    }

    return this.apiClient.fdmEvents(this.generalTabId).pipe(
      catchError((error) => {
        console.error('Error fetching FDM events:', error);
        // Return empty array on error to prevent breaking the flow
        return of([]);
      })
    );
  }

  private _transformHistoryItem(item: FdmEventDto, isRiptCreated: boolean, isFromFdmEvents: boolean = false): ApprovalFlowHistoryDto {
    const reportType = this._general()?.reportType;
    const updatedReportType = ReportTypeOptions.find(option => option.value === reportType)?.label;

    const type = reportType && updatedReportType && item.type
      ? item.type.replace(reportType, updatedReportType)
      : item.type;

    const comment = reportType && updatedReportType && item.comment
      ? item.comment.replace(reportType, updatedReportType)
      : item.comment;

    const created = moment(item.created).isValid()
      ? moment.utc(item.created + 'Z').local()
      : null;

    // const currentState = item.currentState === ApprovalState.RIPTScoredSkipped
    //     ? isRiptCreated ? ApprovalState.RIPTScored : ApprovalState.RIPTSkipped
    //     : item.currentState;
    const currentState = item.currentState;
    const state = isFromFdmEvents ? currentState : this.stepValueOptions[currentState as string];

    return ApprovalFlowHistoryDto.fromJS({
      ...item,
      currentState: isFromFdmEvents ? currentState : (state ?? item.actionItemStatus),
      comment,
      created,
      type,
    });
  }

  getEmailAutocompleteOptions(fieldName: string): string[] {
    return this.emailAutocompleteOptions()[fieldName] || [];
  }

  onEmailAutocompleteSelected(event: MatAutocompleteSelectedEvent, emailFieldName: string, descriptionFieldName: string): void {
    const selectedEmail = event.option.value;
    const descriptionControl = this.form.get(descriptionFieldName);

    if (!selectedEmail || !descriptionControl) {
      return;
    }

    this.loadingFields.update(fields => ({ ...fields, [emailFieldName]: true }));

    this.apiClient.email(selectedEmail.trim()).pipe(
      tap((owners) => {
        if (owners && owners.length > 0) {
          const descriptions = this.emailDescriptionService.processOwnerDescriptions(owners);
          descriptionControl.setValue(descriptions);
        } else {
          descriptionControl.setValue(null);
        }
        this.cdr.markForCheck();
      }),
      catchError(() => of(null)),
      finalize(() => {
        this.loadingFields.update(fields => ({ ...fields, [emailFieldName]: false }));
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  private setupEmailToDescriptionBinding(emailFormControlName: string, descriptionFormControlName: string): void {
    const emailControl = this.form.get(emailFormControlName);
    const descriptionControl = this.form.get(descriptionFormControlName);

    if (emailControl && descriptionControl) {
      emailControl.valueChanges
        .pipe(
          debounceTime(800),
          tap(() => {
            this.loadingFields.update(fields => ({ ...fields, [emailFormControlName]: true }));
            // emailControl.disable({ emitEvent: false });
            // descriptionControl.disable({ emitEvent: false });
          }),
          switchMap((email) => {

            if (!email || email.trim() === '') {
              descriptionControl.patchValue(null);
              this.emailAutocompleteOptions.update(options => ({
                ...options,
                [emailFormControlName]: []
              }));
              this.cdr.markForCheck();
              return of(null).pipe(
                finalize(() => {
                  this.loadingFields.update(fields => ({ ...fields, [emailFormControlName]: false }));
                  emailControl.enable({ emitEvent: false });
                  descriptionControl.enable({ emitEvent: false });
                })
              );
            }

            return this.apiClient.email(email.trim()!).pipe(
              tap((owners) => {
                if (owners && owners.length > 0) {
                  // Keep backend-provided value untouched; normalize only after user edits.
                  const preferredEmail = this.emailDescriptionService.getPreferredEmailForApprovalFlow(owners);
                  if (preferredEmail && emailControl.dirty) {
                    emailControl.setValue(preferredEmail, { emitEvent: false });
                  }

                  // Set descriptions for all items (excluding email but keeping phone)
                  const descriptions = this.emailDescriptionService.processOwnerDescriptions(owners);
                  descriptionControl.setValue(descriptions);

                  const searchText = email.trim().toLowerCase();
                  const emailOptions = owners.flatMap(item => [
                    item.email,
                    item.preferredEmail,
                    item.sharedEmail,
                  ])
                    .filter((emailValue): emailValue is string => !!emailValue && emailValue.trim() !== '')
                    .filter(emailValue => emailValue.toLowerCase().includes(searchText));

                  const distinctEmailOptions = [...new Set(emailOptions.map(e => e.toLowerCase()))];
                  this.emailAutocompleteOptions.update(options => ({
                    ...options,
                    [emailFormControlName]: distinctEmailOptions
                  }));
                } else {
                  this.emailAutocompleteOptions.update(options => ({
                    ...options,
                    [emailFormControlName]: []
                  }));
                }
                this.cdr.markForCheck();
              }),
              catchError(() => {
                this.emailAutocompleteOptions.update(options => ({
                  ...options,
                  [emailFormControlName]: []
                }));
                return of(null);
              }),
              finalize(() => {
                this.loadingFields.update(fields => ({ ...fields, [emailFormControlName]: false }));
                emailControl.enable({ emitEvent: false });
                descriptionControl.enable({ emitEvent: false });
              })
            );
          }),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe();
    }
  }


  private _initOwnerSync(): void {
    this._store.general$
      .pipe(
        map(general => general?.owner),
        filter(owner => !!owner),
        distinctUntilChanged(),
        skip(1),
        filter(() => this.reportType() !== ReportType.AnomalyReport),
        tap(owner => {
          this.form.get('srOpsConsultant')?.setValue(owner!, { emitEvent: false });
          this.loadingFields.update(fields => ({ ...fields, srOpsConsultant: true }));
          this.cdr.markForCheck();
        }),
        switchMap(owner =>
          this.apiClient.email(owner!.trim()).pipe(
            tap(owners => {
              if (owners?.length > 0) {
                const descriptions = this.emailDescriptionService.processOwnerDescriptions(owners);
                this.form.get('srOpsConsultantDescription')?.setValue(descriptions, { emitEvent: false });
              }
              this.cdr.markForCheck();
            }),
            catchError(() => of(null)),
            finalize(() => {
              this.loadingFields.update(fields => ({ ...fields, srOpsConsultant: false }));
              this._triggerApprovalFlowSave();
            })
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private _triggerApprovalFlowSave(): void {
    if (!this.form.valid || !this.generalTabId) return;

    const payload: ReportTask = {
      page: this.step,
      request: this._save()
    };

    if (this._bulkSave.includes(this.step)) {
      this._bulkSave.update(payload);
    } else {
      this._bulkSave.add(payload);
    }
  }

  private _updateSrOpsConsultantWithDescription(email: string | null | undefined): void {
    if (!email || email.trim() === '') {
      this.form.get('srOpsConsultant')?.setValue(null, { emitEvent: false });
      this.form.get('srOpsConsultantDescription')?.setValue(null, { emitEvent: false });
      return;
    }

    this.form.get('srOpsConsultant')?.setValue(email, { emitEvent: false });
    this.loadingFields.update(fields => ({ ...fields, srOpsConsultant: true }));

    this.apiClient.email(email.trim()).pipe(
      tap(owners => {
        if (owners?.length > 0) {
          const descriptions = this.emailDescriptionService.processOwnerDescriptions(owners);
          this.form.get('srOpsConsultantDescription')?.setValue(descriptions, { emitEvent: false });
        }
      }),
      catchError(() => of(null)),
      finalize(() => {
        this.loadingFields.update(fields => ({ ...fields, srOpsConsultant: false }));
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  addNewEndorser(email = '', disabled = false) {
    this.endorsers.push(this.fb.control({ value: email, disabled }, [Validators.required, emailValidator()]));
  }

  clearEndorsers() {
    while(this.endorsers.length > 0) {
      this.endorsers.removeAt(0);
    }
  }

  deleteEndorser(idx: number) {
    this.endorsers.removeAt(idx);
  }

  onDistributionListTitleSelected(item: DistributionList): void {
    if (this.isDistributionSelectionDisabled()) {
      return;
    }

    const parts = [item.turnaroundEmailsTo, item.commonEmailsTo].filter(Boolean);
    const informedEmailsTo = parts.join('; ');
    this.form.patchValue({
      informedEmailsTo,
      informedEmailsCc: item.commonEmailsCc ?? '',
    });
  }

  private _loadDistributionListTitles(): void {
    this.apiClient.distributionListAll(false)
      .pipe(
        catchError(() => of([])),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((list: DistributionList[]) => {
        this.distributionListOptions.set(list ?? []);
      });
  }

  private _getDistributionlist() {
    combineLatest([
      this.apiClient.distributionListAll(true),
      this._store.equipment$
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([list, equipment]) => {
        const facility = equipment?.facility;
        const unit = equipment?.e1DataEquipments?.[0]?.unit;

        const res = list.find(item => item.facility === facility && item.unit === unit);

        this._matchedDistribution = res
          ? { to: res.commonEmailsTo, cc: res.commonEmailsCc }
          : null;

        this._applyDistributionDefaultsIfEmpty();
      });
  }

  private _applyDistributionDefaultsIfEmpty(): void {
    if (!this.form || !this._matchedDistribution) {
      return;
    }

    const toControl = this.form.get('informedEmailsTo');
    const ccControl = this.form.get('informedEmailsCc');

    const patch: { informedEmailsTo?: string; informedEmailsCc?: string } = {};

    if (toControl && !toControl.value && this._matchedDistribution.to) {
      patch.informedEmailsTo = this._matchedDistribution.to;
    }

    if (ccControl && !ccControl.value && this._matchedDistribution.cc) {
      patch.informedEmailsCc = this._matchedDistribution.cc;
    }

    if (Object.keys(patch).length > 0) {
      this.form.patchValue(patch, { emitEvent: false });
    }
  }

  filters: Record<string, FilterResponse  | null> = {};
  tempTableData: any[] = [];

  applyFilter(res: FilterResponse | null, column: string) {
    this.filters[column] = res;
    this.historyDataSource.data = filterTable(this.tempTableData, column, res);
  }

  isActive(column: string) {    
    return this.filters[column] != null;
  }
}
